class ClarityService {
  constructor() {
    this.isInitialized = false
    this.projectId = process.env.REACT_APP_CLARITY_ID
    this.eventQueue = []
    this.retryAttempts = 0
    this.maxRetries = 5
    this.consentGiven = false
    this.isRestrictedRegion = null // Track if user is in restricted region
    // Microsoft Clarity will begin enforcing cookie consent requirements in the European Economic Area (EEA), UK, and Switzerland October 31st, 2025

    // Initialize Google Consent Mode v2 if available
    this.initializeConsentMode()

    // Check for existing consent and region
    this.checkExistingConsent()
  }

  // Initialize Google Consent Mode v2 (Microsoft's recommended approach)
  initializeConsentMode() {
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || []
      function gtag() {
        window.dataLayer.push(arguments)
      }
      window.gtag = window.gtag || gtag

      // Set default consent state (denied until user consents)
      gtag('consent', 'default', {
        analytics_storage: 'denied',
        ad_storage: 'denied',
        wait_for_update: 500, // Wait 500ms for consent update
      })
    }
  }

  // Check if user has already given consent
  async checkExistingConsent() {
    try {
      // Check if user is in restricted region
      await this.checkRestrictedRegion()
      const existingConsent = this.getCookie('cookie-consent')
      if (existingConsent) {
        const consent = JSON.parse(existingConsent)
        if (consent.analytics) {
          this.consentGiven = true
          this.updateGoogleConsentMode(true)
          this.init()
        }
      } else if (!this.isRestrictedRegion) {
        // If user is not in restricted region, automatically grant consent
        this.consentGiven = true
        this.updateGoogleConsentMode(true)
        this.init()
      }
    } catch (error) {
      console.error('Error checking existing consent:', error)
      // Conservative fallback: wait for explicit consent
      this.isRestrictedRegion = true
    }
  }

  // Check if user is in a restricted region (same logic as cookie banner)
  async checkRestrictedRegion() {
    try {
      // Check cached result first
      const cachedResult = this.getCookie('geo-check')
      if (cachedResult !== null) {
        this.isRestrictedRegion = cachedResult === 'true'
        return this.isRestrictedRegion
      }

      // Try timezone detection as primary method
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
      const europeanTimezones = [
        'Europe/',
        'Atlantic/Reykjavik',
        'Atlantic/Faroe',
        'Atlantic/Canary',
      ]

      const isEuropeanTimezone = europeanTimezones.some((tz) =>
        timezone.startsWith(tz)
      )

      if (isEuropeanTimezone) {
        this.isRestrictedRegion = true
        return true
      }

      // Try IP geolocation API as fallback
      try {
        const response = await fetch('https://ipapi.co/country_code/', {
          method: 'GET',
          timeout: 3000,
        })

        if (response.ok) {
          const countryCode = await response.text()
          const restrictedCountries = [
            'AT',
            'BE',
            'BG',
            'HR',
            'CY',
            'CZ',
            'DK',
            'EE',
            'FI',
            'FR',
            'DE',
            'GR',
            'HU',
            'IS',
            'IE',
            'IT',
            'LV',
            'LI',
            'LT',
            'LU',
            'MT',
            'NL',
            'NO',
            'PL',
            'PT',
            'RO',
            'SK',
            'SI',
            'ES',
            'SE',
            'GB',
            'CH',
          ]

          this.isRestrictedRegion = restrictedCountries.includes(
            countryCode.trim().toUpperCase()
          )
          return this.isRestrictedRegion
        }
      } catch (apiError) {
        // console.log('IP geolocation API failed, using timezone fallback')
      }

      // Conservative fallback
      this.isRestrictedRegion = isEuropeanTimezone
      return this.isRestrictedRegion
    } catch (error) {
      console.error('Error checking restricted region:', error)
      // Conservative approach: assume restricted region
      this.isRestrictedRegion = true
      return true
    }
  }

  // Cookie utility function
  getCookie(name) {
    const value = `; ${document.cookie}`
    const parts = value.split(`; ${name}=`)
    if (parts.length === 2) return parts.pop().split(';').shift()
    return null
  }

  // Update Google Consent Mode v2
  updateGoogleConsentMode(hasConsent) {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: hasConsent ? 'granted' : 'denied',
      })
    }
  }

  // Initialize Clarity only after consent (or if not in restricted region)
  updateConsent(consent) {
    const newConsentStatus = consent?.analytics || false

    if (newConsentStatus !== this.consentGiven) {
      this.consentGiven = newConsentStatus

      // Update Google Consent Mode
      this.updateGoogleConsentMode(this.consentGiven)

      if (this.consentGiven && !this.isInitialized) {
        // User granted consent, initialize Clarity
        this.init()
      } else if (!this.consentGiven && this.isInitialized) {
        // User revoked consent, stop Clarity
        this.stopClarity()
      } else if (this.consentGiven && this.isInitialized) {
        // Update consent signal using Clarity API v2
        this.sendConsentSignal(true)
      }
    }
  }

  // Send consent signal using Clarity's native API v2
  sendConsentSignal(hasConsent) {
    if (typeof window !== 'undefined' && window.clarity) {
      try {
        // Microsoft Clarity Consent API v2
        window.clarity('set', 'consent', hasConsent)
        // console.log(
        //   `Clarity consent signal sent: ${hasConsent ? 'granted' : 'denied'}`
        // )
        // Also send region info for compliance tracking
        if (this.isRestrictedRegion !== null) {
          window.clarity('set', 'region_restricted', this.isRestrictedRegion)
        }

        // console.log(
        //   `Clarity consent signal sent: ${
        //     hasConsent ? 'granted' : 'denied'
        //   } (restricted region: ${this.isRestrictedRegion})`
        // )
      } catch (error) {
        console.error('Failed to send consent signal to Clarity:', error)
      }
    }
  }

  // Stop Clarity if consent is revoked
  stopClarity() {
    if (this.isInitialized && typeof window !== 'undefined' && window.clarity) {
      try {
        // Send consent revocation signal
        this.sendConsentSignal(false)

        // Clear user data and stop tracking
        window.clarity('stop')

        this.isInitialized = false
        // console.log('Clarity stopped due to consent revocation')
      } catch (error) {
        console.error('Failed to stop Clarity:', error)
      }
    }
  }

  init() {
    if (!this.projectId || typeof window === 'undefined') {
      console.warn(
        'Clarity: Project ID not found or not in browser environment'
      )
      return
    }

    // Only require explicit consent for restricted regions
    if (this.isRestrictedRegion && !this.consentGiven) {
      // console.log(
      //   'Clarity: Waiting for user consent before initialization (restricted region)'
      // )
      return
    }
    // Load Clarity script
    this.loadClarityScript()

    this.waitForClarity()
      .then(() => {
        this.isInitialized = true

        // Send initial consent signal using Clarity API v2
        this.sendConsentSignal(this.consentGiven || !this.isRestrictedRegion)
        this.setClarityContext()

        this.setupEventListeners()
        this.processEventQueue()
        // console.log('Clarity service initialized with proper consent signals')
      })
      .catch((error) => {
        console.error('Failed to initialize Clarity:', error)
      })
  }

  loadClarityScript() {
    // Check if script already exists
    if (
      document.querySelector(`script[src*="clarity.ms/tag/${this.projectId}"]`)
    ) {
      return
    }

    try {
      const script = document.createElement('script')
      script.type = 'text/javascript'
      script.async = true
      script.innerHTML = `
        (function(c,l,a,r,i,t,y){
          c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
          t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
          y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
        })(window, document, "clarity", "script", "${this.projectId}");
      `
      document.head.appendChild(script)
    } catch (error) {
      console.error('Failed to load Clarity script:', error)
    }
  }

  waitForClarity() {
    return new Promise((resolve, reject) => {
      const checkClarity = () => {
        if (window.clarity) {
          resolve()
        } else if (this.retryAttempts < this.maxRetries) {
          this.retryAttempts++
          setTimeout(checkClarity, 200 * this.retryAttempts)
        } else {
          reject(new Error('Clarity failed to load after maximum retries'))
        }
      }
      checkClarity()
    })
  }

  // Add stable tags so one Clarity project can be filtered by hostname/domain.
  setClarityContext() {
    if (!this.isInitialized || typeof window === 'undefined' || !window.clarity) {
      return
    }

    try {
      const hostname = window.location.hostname || 'unknown'
      const environment = process.env.REACT_APP_ENVIRONMENT || 'prod'

      window.clarity('set', 'domain', hostname)
      window.clarity('set', 'environment', environment)
      window.clarity('set', 'site_group', 'gcc-taxlaws')
      window.clarity('event', 'clarity_context_set')
    } catch (error) {
      console.error('Error setting Clarity context:', error)
    }
  }

  processEventQueue() {
    while (this.eventQueue.length > 0) {
      const event = this.eventQueue.shift()
      if (event.type === 'event') {
        this.trackEvent(event.eventName, event.properties)
      } else if (event.type === 'userId') {
        this.setUserId(event.userId)
      }
    }
  }

  setupEventListeners() {
    // Track page visibility changes for session continuity
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.trackEvent('page_visible', {
          timestamp: new Date().toISOString(),
          wasAuthFlow: !!sessionStorage.getItem('auth_flow_started'),
        })
      } else {
        this.trackEvent('page_hidden', {
          timestamp: new Date().toISOString(),
          inAuthFlow: !!sessionStorage.getItem('auth_flow_started'),
        })
      }
    })

    // Track when user returns from external OAuth2 providers
    if (
      document.referrer &&
      (document.referrer.includes('accounts.google.com') ||
        document.referrer.includes('linkedin.com'))
    ) {
      this.trackEvent('oauth2_provider_return', {
        provider: this.extractProvider(document.referrer),
        timestamp: new Date().toISOString(),
      })
    }
  }

  extractProvider(referrer) {
    if (referrer.includes('google.com')) return 'google'
    if (referrer.includes('linkedin.com')) return 'linkedin'
    return 'unknown'
  }

  // Track events only if consent given (or not in restricted region)
  trackEvent(eventName, properties = {}) {
    const canTrack = this.consentGiven || !this.isRestrictedRegion
    if (!canTrack) {
      this.eventQueue.push({ type: 'event', eventName, properties })
      return
    }

    if (!this.isInitialized || !window.clarity) {
      this.eventQueue.push({ type: 'event', eventName, properties })

      if (this.eventQueue.length <= 50) {
        setTimeout(() => this.trackEvent(eventName, properties), 1000)
      }
      return
    }

    try {
      // Use correct Clarity event tracking API
      window.clarity('event', eventName, properties)
      // console.log('Clarity event tracked:', eventName, properties)
    } catch (error) {
      console.error('Error tracking Clarity event:', error)
    }
  }

  // Set user ID only if consent given (or not in restricted region)
  setUserId(userId) {
    if (!userId) return

    const canTrack = this.consentGiven || !this.isRestrictedRegion
    if (!canTrack) {
      this.eventQueue.push({ type: 'userId', userId })
      return
    }

    if (!this.isInitialized || !window.clarity) {
      setTimeout(() => this.setUserId(userId), 1000)
      return
    }

    try {
      // Use correct Clarity identify API
      window.clarity('identify', userId)
      if (canTrack) {
        localStorage.setItem('clarity_user_id', userId)
      }
      // console.log('Clarity user ID set:', userId)
    } catch (error) {
      console.error('Error setting Clarity user ID:', error)
    }
  }

  // Authentication flow tracking
  trackAuthStart(provider) {
    sessionStorage.setItem('auth_provider', provider)
    sessionStorage.setItem('auth_start_time', Date.now().toString())

    this.trackEvent('auth_start', {
      provider,
      timestamp: new Date().toISOString(),
      url: window.location.href,
    })
  }

  trackAuthSuccess(user) {
    const authStartTime = sessionStorage.getItem('auth_start_time')
    const authDuration = authStartTime
      ? Date.now() - parseInt(authStartTime)
      : 0

    this.trackEvent('auth_success', {
      userId: user.id || user.email,
      userEmail: user.email,
      authDuration,
      timestamp: new Date().toISOString(),
      provider: sessionStorage.getItem('auth_provider'),
    })

    this.setUserId(user.id || user.email)

    sessionStorage.removeItem('auth_start_time')
    sessionStorage.removeItem('auth_provider')
  }

  trackAuthFailure(error) {
    const authStartTime = sessionStorage.getItem('auth_start_time')
    const authDuration = authStartTime
      ? Date.now() - parseInt(authStartTime)
      : 0

    this.trackEvent('auth_failure', {
      error:
        typeof error === 'string' ? error : error.message || 'Unknown error',
      authDuration,
      timestamp: new Date().toISOString(),
      provider: sessionStorage.getItem('auth_provider'),
    })
  }

  // Page view tracking
  trackPageView(path, additionalData = {}) {
    this.setClarityContext()
    this.trackEvent('page_view', {
      path,
      hostname: window.location.hostname,
      timestamp: new Date().toISOString(),
      referrer: document.referrer,
      ...additionalData,
    })
  }

  // Search tracking
  trackSearch(query, results = 0) {
    this.trackEvent('search', {
      query: query ? query.substring(0, 50) : '',
      resultsCount: results,
      timestamp: new Date().toISOString(),
    })
  }

  // Content view tracking
  trackContentView(contentType, contentId, contentTitle) {
    this.trackEvent('content_view', {
      contentType,
      contentId: contentId ? contentId.substring(0, 50) : '',
      contentTitle: contentTitle ? contentTitle.substring(0, 100) : '',
      timestamp: new Date().toISOString(),
    })
  }

  // Session continuity
  trackSessionContinuity() {
    const sessionId =
      sessionStorage.getItem('session_id') || `session_${Date.now()}`
    const isNewSession = !sessionStorage.getItem('session_id')

    sessionStorage.setItem('session_id', sessionId)

    this.trackEvent('session_continuity', {
      sessionId,
      timestamp: new Date().toISOString(),
      isNewSession,
    })
  }

  // OAuth2 return handling
  handleOAuth2Return() {
    this.trackEvent('oauth2_return', {
      timestamp: new Date().toISOString(),
      url: window.location.href,
      referrer: document.referrer,
      hasAuthFlow: !!sessionStorage.getItem('auth_flow_started'),
    })
    const canTrack = this.consentGiven || !this.isRestrictedRegion
    if (window.clarity && canTrack) {
      try {
        const storedUserId = localStorage.getItem('clarity_user_id')
        if (storedUserId && !storedUserId.startsWith('anonymous_')) {
          this.setUserId(storedUserId)
        }
        this.trackSessionContinuity()
      } catch (error) {
        console.error('Error handling OAuth2 return:', error)
      }
    }
  }

  // New tab tracking
  trackNewTabOpen(url, contentType) {
    this.trackEvent('new_tab_open', {
      targetUrl: url ? url.substring(0, 100) : '',
      contentType,
      parentUrl: window.location.href.substring(0, 100),
      timestamp: new Date().toISOString(),
    })
  }

  // Debug status
  getStatus() {
    return {
      isInitialized: this.isInitialized,
      consentGiven: this.consentGiven,
      isRestrictedRegion: this.isRestrictedRegion,
      clarityLoaded: !!window.clarity,
      queuedEvents: this.eventQueue.length,
      retryAttempts: this.retryAttempts,
      storedUserId: this.consentGiven || !this.isRestrictedRegion
          ? localStorage.getItem('clarity_user_id')
          : null,
      consentModeEnabled: !!window.gtag,
    }
  }
}

const clarityService = new ClarityService()
export default clarityService
